import React, { createRef } from 'react';
import _ from 'lodash';
import JsonSchemaForm from '@rjsf/core';
import Isvg from 'react-inlinesvg';
import AddButton from '@rjsf/core/components/AddButton';
import Button from 'app/components/general/Button';
import InputGroupBlockSchema from 'app/components/general/InputGroupBlockSchema';
import ModalConfirm from 'app/components/general/ModalConfirm';
import classnames from 'classnames';

import PlusIcon from 'app/images/icons/plus.svg';

const customFields = {
  StringField: InputGroupBlockSchema,
  currency: InputGroupBlockSchema,
  datetime: InputGroupBlockSchema,
  select: InputGroupBlockSchema,
  NumberField: InputGroupBlockSchema,
  BooleanField: InputGroupBlockSchema,
};

interface ISchemaFormProps {
  hasSaved?: boolean;
  hasFailed?: boolean;
  schema?: any;
  errors?: any;
  uiSchema?: any;
  formData?: any;
  originalFormData?: any;
  handleChange?: Function;
  handleSubmit?: Function;
  handleError?: Function;
  handleDelete?: Function;
  isLoading?: boolean;
  renderButtons?: boolean;
  showAddAnotherButton?: boolean;
  liveValidate?: boolean;
  isValid?: boolean;
  buttonText?: string;
  addAnotherText?: string;
  modalDeleteTitle?: string;
  modalDeleteDescription?: string;
  modalDeleteButtonText?: string;
}

interface ISchemaFormState {
  deleteModalOpen: boolean;
  saving: boolean;
  deleting: boolean;
  hasFailedValidation: boolean;
  changed: boolean;
}


function ArrayFieldTemplate(props) {
  return (
    <fieldset className={`${props.className}`} id={props.idSchema.$id}>
      <div className="row">
        <div className="col">
          <h4 className="d-inline-block text-black">{props.uiSchema['ui:title'] || props.title}</h4>
        </div>
      </div>
      {props.schema.description && (
        <div className="row with-margin-top-10">
          <div className="col">
            <div className="">{props.schema.description}</div>
          </div>
        </div>
      )}
      <div className="row">
        <div className="col">
          <div className="divider full less-margin" />
        </div>
      </div>
      <div
        className="row"
        key={`array-item-list-${props.idSchema.$id}`}
      >
        <div className="col">
          {/* {props.items && props.items.map((p) => DefaultArrayItem(p))} */}
          {props.items.map((element) => (
            <div className="row">
              <div className="col">
                <div className="field-group">
                  {element.children}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {props.canAdd && (
        <Button onClick={props.onAddClick} disabled={props.disabled || props.readonly} noLoading className="btn-thinner btn-2 with-margin-bottom">
          <Isvg src={PlusIcon} className="btn-icon" />
          Add
        </Button>
      )}
    </fieldset>
  );
}

function ObjectFieldTemplate(props) {
  const { TitleField, DescriptionField } = props;
  return (
    <fieldset id={props.idSchema.$id}>
      {(props.uiSchema['ui:title'] || props.title) && (
        <TitleField
          id={`${props.idSchema.$id}__title`}
          title={props.title || props.uiSchema['ui:title']}
          required={props.required}
          formContext={props.formContext}
        />
      )}
      {props.description && (
        <DescriptionField
          id={`${props.idSchema.$id}__description`}
          description={props.description}
          formContext={props.formContext}
        />
      )}
      <div className="row">
        {props.properties?.map((prop) => prop.content)}
      </div>
    </fieldset>
  );
}

function CustomFieldTemplate(props) {
  const {
    id, classNames, label, help, required, description, errors, children, uiSchema,
  } = props;
  const rootObject = props.id === 'root' && (props.schema?.type === 'object' || props.schema?.type === undefined);
  return (
    <div className={classnames({ col: !rootObject }, { 'col-12': !rootObject && (classNames === undefined || !classNames.includes('col-')) }, classNames)}>
      {/* <label htmlFor={id}>
          {label}
          {required ? '*' : null}
        </label> */}
      {/* {description} */}
      {children}
      {errors}
      {help}
    </div>
  );
}

export default class SchemaForm extends React.Component<
  ISchemaFormProps,
  ISchemaFormState
> {
  timeout;

  static defaultProps: ISchemaFormProps = {
    renderButtons: true,
    isValid: true,
    liveValidate: false,
    buttonText: 'Save',
    addAnotherText: 'Save & Add another',
    isLoading: false,
    hasSaved: false,
    hasFailed: false,
    showAddAnotherButton: false,
  };

  state: ISchemaFormState = {
    deleteModalOpen: false,
    saving: false,
    changed: false,
    deleting: false,
    hasFailedValidation: false,
  }

  componentWillUnmount(): void {
    clearTimeout(this.timeout);
  }

  handleCancel = () => {
    this.props.handleChange({
      formData: this.props.originalFormData,
    });
  };

  handleOpenDeleteModal = () => {
    this.setState((prevState) => ({
      deleteModalOpen: true,
    }));
  };

  handleCloseDeleteModal = () => {
    this.setState((prevState) => ({
      deleteModalOpen: false,
    }));
  };

  transformErrors = (errors): any => errors.map((error) => {
    if (error.name === 'required') {
      error.message = 'is required';
    }
    return error;
  })

  // toggleChanged = ():void => {
  //   this.timeout = setTimeout(() => {
  //     this.setState({ currentState: BUTTON_STATE.nothing, transitioned: true });
  //   }, this.props.savedDuration);
  // }

  handleSubmit = (data, e): void => {
    this.setState({ saving: true, changed: true, hasFailedValidation: false }, async () => {
      if (this.props.handleSubmit !== undefined) {
        setTimeout(() => {
          this.props.handleSubmit(data, e);
        }, 1);
      }
      this.timeout = setTimeout(() => {
        this.setState({ saving: false, changed: false });
      }, 1500);
    });
  }

  handleDelete = (e): void => {
    this.setState({ deleting: true, changed: true, hasFailedValidation: false }, async () => {
      if (this.props.handleDelete !== undefined) {
        setTimeout(() => {
          this.props.handleDelete(e);
        }, 1);
      }
      this.timeout = setTimeout(() => {
        this.setState({ deleting: false, changed: false });
      }, 1500);
    });
  }

  handleError = (errors): void => {
    // console.log(errors);
    this.setState({ saving: false, hasFailedValidation: true });
    if (this.props.handleError !== undefined) {
      this.props.handleError(errors);
    }
  }

  renderBackendErrors = (): React.ReactNode => {
    if (this.props.errors !== undefined && !_.isEmpty(this.props.errors)) {
      return (
        <span className="backend-errors">
          {_.map(this.props.errors, (value, key) => (
            <span key={key}>
              {_.capitalize(key)}
              :
              {' '}
              {value.join(',')}
              <br />
            </span>
          ))}
        </span>
      );
    }
    return null;
  };

  renderDeleteModal = (): React.ReactNode => (
    <ModalConfirm
      title={this.props.modalDeleteTitle}
      description={this.props.modalDeleteDescription}
      buttonText={this.props.modalDeleteButtonText}
      btnClassName="btn-error"
      isOpen={this.state.deleteModalOpen}
      isLoading={this.state.deleting}
      hasSaved={this.props.hasSaved}
      hasFailedValidation={this.state.hasFailedValidation}
      hasFailed={this.props.hasFailed}
      handleSubmit={this.handleDelete}
      handleCancel={this.handleCloseDeleteModal}
    />
  );

  render(): React.ReactNode {
    const uiSchema = this.props.uiSchema || {};
    _.map(this.props.schema.properties, (value, key) => {
      uiSchema[key] = {
        ...uiSchema[key],
        'ui:options': {
          label: false,
          title: false,
          description: true,
        },
      };
    });
    const changed = ((!_.isEqual(this.props.formData, this.props.originalFormData))
      || this.state.changed)
      && !(this.state.deleteModalOpen || this.state.deleting);
    // console.log('1');
    // console.log(this.props.formData !== this.props.originalFormData);
    // console.log('2');
    // console.log(this.state.changed);
    // console.log('3');
    // console.log(changed);
    return (
      <JsonSchemaForm
        fields={customFields}
        FieldTemplate={CustomFieldTemplate}
        ObjectFieldTemplate={ObjectFieldTemplate}
        ArrayFieldTemplate={ArrayFieldTemplate}
        schema={this.props.schema}
        uiSchema={uiSchema}
        transformErrors={this.transformErrors}
        onChange={this.props.handleChange}
        onSubmit={this.handleSubmit}
        onError={this.handleError}
        liveValidate={this.props.liveValidate}
        showErrorList={false}
        formData={this.props.formData}
      >
        {this.props.children}
        {this.renderBackendErrors()}
        {this.renderDeleteModal()}
        {this.props.renderButtons && (
          <div className="row">
            {this.props.handleDelete !== undefined && (
              <div className="col btn-flex-group">
                <div className={classnames('btn-flex-container', { reverse: changed })}>
                  <Button
                    className="btn-standard btn-error"
                    isLoading={this.state.deleting}
                    hasSaved={this.props.hasSaved}
                    hasFailedValidation={this.state.hasFailedValidation}
                    hasFailed={this.props.hasFailed}
                    type="button"
                    onClick={this.handleOpenDeleteModal}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            )}
            <div
              className={classnames('col btn-flex-group', {
                'd-none': !changed,
              })}
            >
              <div className="btn-flex-container">
                <Button
                  className="btn-standard btn-1"
                  isLoading={this.state.saving}
                  type="submit"
                  name="submit"
                  hasSaved={this.props.hasSaved}
                  hasFailedValidation={this.state.hasFailedValidation}
                  hasFailed={this.props.hasFailed}
                  disabled={!this.props.isValid}
                >
                  {this.props.buttonText}
                </Button>
                {this.props.showAddAnotherButton && (
                  <Button
                    className="btn-standard btn-1"
                    isLoading={this.state.saving}
                    type="submit"
                    name="submitAddAnother"
                    hasSaved={this.props.hasSaved}
                    hasFailedValidation={this.state.hasFailedValidation}
                    hasFailed={this.props.hasFailed}
                    disabled={!this.props.isValid}
                  >
                    {this.props.addAnotherText}
                  </Button>
                )}
                <Button
                  className="btn-standard btn-2"
                  noLoading
                  onClick={this.handleCancel}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </JsonSchemaForm>
    );
  }
}
